import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useCartStore } from '../../store/cart.store';
import { CartDrawer } from '../cart/CartDrawer';
import { SearchResults } from '../search/SearchResults';
import { ProductsService } from '../../services/products/products.service';
import type { ProductSearchResult } from '../../services/products/products.service';
import { 
  Home,
  ShoppingBag,
  Search, 
  ShoppingCart, 
  Package, 
  User,
  Heart,
  Bell,
  LogOut,
  Truck,
  FileText
} from 'lucide-react';
import logoSinTexto from '../../assets/Logo_sin_texto.png';
import textoLogo from '../../assets/Texto logo.png';

interface ClienteLayoutProps {
  children: React.ReactNode;
}

export const ClienteLayout: React.FC<ClienteLayoutProps> = ({ children }) => {
  const { theme } = useTheme();
  const { getTotalItems, openCart } = useCartStore();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [userData, setUserData] = useState<any>(null);

  // Obtener datos del usuario
  useEffect(() => {
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      setUserData(JSON.parse(userDataStr));
    }
  }, []);

  const totalItems = getTotalItems();

  const sidebarItems = [
    { 
      name: 'Dashboard', 
      path: '/cliente', 
      icon: <Home className="w-5 h-5" />,
      description: 'Panel principal'
    },
    { 
      name: 'Hacer Compras', 
      path: '/cliente/compras', 
      icon: <ShoppingBag className="w-5 h-5" />,
      description: 'Explorar productos'
    },
    { 
      name: 'Historial', 
      path: '/cliente/historial', 
      icon: <FileText className="w-5 h-5" />,
      description: 'Pedidos anteriores'
    },
    { 
      name: 'Mis Pedidos', 
      path: '/cliente/pedidos', 
      icon: <Package className="w-5 h-5" />,
      description: 'Gestionar pedidos'
    },
    { 
      name: 'Favoritos', 
      path: '/cliente/favoritos', 
      icon: <Heart className="w-5 h-5" />,
      description: 'Productos guardados'
    },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    window.location.href = '/';
  };

  // Búsqueda
  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setShowResults(true);

    try {
      const response = await ProductsService.search(query, 8);
      if (response.success) {
        setSearchResults(response.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (value.trim()) {
      setTimeout(() => performSearch(value), 300);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const handleCloseResults = () => {
    setShowResults(false);
  };

  const handleAddToCartFromSearch = (product: ProductSearchResult) => {
    alert(`${product.nombre} agregado al carrito`);
    setShowResults(false);
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } border-b px-6 py-8 sticky top-0 z-50`}>
        <div className="flex items-center justify-between">
          {/* Logo y navegación */}
          <div className="flex items-center space-x-6">
            {/* Logo */}
            <Link to="/cliente" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-105 bg-white shadow-md p-1">
                <img 
                  src={logoSinTexto} 
                  alt="ConfiFarma Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <img 
                  src={textoLogo} 
                  alt="ConfiFarma Amiga" 
                  className={`h-7 w-auto transition-all duration-300 ${
                    theme === 'dark' ? 'brightness-110' : ''
                  }`}
                />
              </div>
            </Link>

            {/* Navegación principal */}
            <nav className="hidden lg:flex items-center space-x-1">
              {sidebarItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-all duration-300 text-sm ${
                    isActive(item.path)
                      ? theme === 'dark'
                        ? 'bg-emerald-600 text-white shadow-lg'
                        : 'bg-emerald-500 text-white shadow-lg'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {item.icon}
                  <span>{item.name}</span>
                </Link>
              ))}
            </nav>
          </div>
          
          {/* Búsqueda centrada */}
          <div className="flex-1 max-w-2xl mx-8 relative">
            <input
              type="text"
              placeholder="Buscar medicamentos, productos..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className={`w-full pl-4 pr-12 py-3 rounded-xl border-2 transition-all duration-300 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                  : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
              }`}
            />
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            
            {/* Resultados de búsqueda */}
            <SearchResults
              results={searchResults}
              isLoading={isSearching}
              query={searchQuery}
              isVisible={showResults}
              onClose={handleCloseResults}
              onAddToCart={handleAddToCartFromSearch}
            />
          </div>

          {/* Acciones del lado derecho */}
          <div className="flex items-center space-x-4">
            {/* Información del usuario */}
            {userData && (
              <div className={`flex items-center space-x-3 px-3 py-2 rounded-lg ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-emerald-50'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  theme === 'dark'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-emerald-500 text-white'
                }`}>
                  <User className="w-4 h-4" />
                </div>
                <div className="hidden sm:block">
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {userData.nombreCompleto || userData.nombres || userData.nombre || 'Cliente'}
                  </p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-emerald-600'}`}>
                    Cliente
                  </p>
                </div>
              </div>
            )}
            
            {/* Notificaciones */}
            <button className={`relative p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}>
              <Bell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* Carrito */}
            <button 
              onClick={openCart}
              className={`relative p-2 rounded-lg transition-colors ${
                theme === 'dark'
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {totalItems > 99 ? '99+' : totalItems}
                </span>
              )}
            </button>

            {/* Theme toggle */}
            {/* Logout */}
            <button 
              onClick={handleLogout}
              className={`p-2 rounded-lg transition-colors ${
                theme === 'dark'
                  ? 'text-red-400 hover:text-red-300 hover:bg-red-900/50'
                  : 'text-red-600 hover:text-red-700 hover:bg-red-100'
              }`}
              title="Cerrar Sesión"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Contenido Principal */}
      <main className={`w-full`}>
        {children}
      </main>

      {/* Cart Drawer */}
      <CartDrawer />
    </div>
  );
};